import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, MapPin } from "lucide-react";

const AdminCustomTrips = () => {
  const [trips, setTrips] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("custom_trip_requests")
      .select("*")
      .order("created_at", { ascending: false });
    setTrips(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("custom_trip_requests").update({ status }).eq("id", id);
    if (error) toast.error("Erreur: " + error.message);
    else { toast.success("Statut mis à jour"); load(); }
  };

  const buildRoute = (t: any) => {
    const waypoints: string[] = t.waypoints || [];
    const durations: number[] = t.waypoint_durations || [];
    const parts = [t.departure_city];
    waypoints.forEach((wp: string, i: number) => {
      const d = durations[i];
      parts.push(d && d > 1 ? `${wp} (${d}j)` : wp);
    });
    parts.push(t.destination_city);
    return parts.filter(Boolean).join(" → ");
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">Voyages sur Mesure</CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </Button>
      </CardHeader>
      <CardContent>
        {trips.length === 0 ? (
          <p className="text-muted-foreground font-body text-center py-8">Aucune demande.</p>
        ) : (
          <div className="space-y-4">
            {trips.map((t) => (
              <div key={t.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <span className="font-body font-semibold text-foreground">{t.name}</span>
                    <span className="ml-3 font-body text-sm text-muted-foreground">{t.phone}</span>
                    {t.email && <span className="ml-3 font-body text-sm text-muted-foreground">{t.email}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-body text-xs text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString("fr-FR")}
                    </span>
                    <Select defaultValue={t.status} onValueChange={(val) => updateStatus(t.id, val)}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="confirmed">Confirmé</SelectItem>
                        <SelectItem value="completed">Terminé</SelectItem>
                        <SelectItem value="cancelled">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="font-body text-sm text-foreground">{buildRoute(t)}</span>
                </div>
                <div className="flex flex-wrap gap-4 font-body text-xs text-muted-foreground">
                  <span>Passagers: {t.passengers}</span>
                  {t.departure_date && <span>Départ: {t.departure_date}</span>}
                  {t.return_date && <span>Retour: {t.return_date}</span>}
                </div>
                {t.notes && (
                  <p className="font-body text-sm text-muted-foreground bg-muted/50 rounded p-2">{t.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCustomTrips;
